import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const multiSelectVariants = cva(
  'flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 py-2 transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-900',
  {
    variants: {
      variant: {
        default: 'border-gray-200 dark:border-gray-800',
        error: 'border-error-500 focus-within:ring-error-500',
        success: 'border-success-500 focus-within:ring-success-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface MultiSelectOption {
  id: string | number;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof multiSelectVariants> {
  options: MultiSelectOption[];
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  maxHeight?: string;
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      className,
      variant,
      options,
      value = [],
      onChange,
      placeholder = 'Select options...',
      disabled = false,
      searchable = false,
      maxHeight = '16rem',
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    const selectedOptions = options.filter((opt) => value.includes(opt.id));
    const filteredOptions = searchable
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;

    const toggleOption = (id: string | number) => {
      if (disabled) return;
      
      const newValue = value.includes(id)
        ? value.filter((v) => v !== id)
        : [...value, id];
      
      onChange?.(newValue);
    };

    const removeOption = (id: string | number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onChange?.(value.filter((v) => v !== id));
    };

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    React.useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    return (
      <div ref={containerRef} className="relative w-full" {...props}>
        <div
          ref={ref}
          className={cn(multiSelectVariants({ variant }), className)}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-disabled={disabled}
        >
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <div
                  key={option.id}
                  className="group flex items-center justify-center rounded-full border border-transparent bg-gray-100 py-1 pl-2.5 pr-2 text-sm text-gray-800 hover:border-gray-200 dark:bg-gray-800 dark:text-white dark:hover:border-gray-700"
                >
                  <span>{option.label}</span>
                  <button
                    type="button"
                    onClick={(e) => removeOption(option.id, e)}
                    disabled={disabled}
                    className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    aria-label={`Remove ${option.label}`}
                  >
                    <svg
                      className="h-3.5 w-3.5 fill-current"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3.40717 4.46881C3.11428 4.17591 3.11428 3.70104 3.40717 3.40815C3.70006 3.11525 4.17494 3.11525 4.46783 3.40815L6.99943 5.93975L9.53095 3.40822C9.82385 3.11533 10.2987 3.11533 10.5916 3.40822C10.8845 3.70112 10.8845 4.17599 10.5916 4.46888L8.06009 7.00041L10.5916 9.53193C10.8845 9.82482 10.8845 10.2997 10.5916 10.5926C10.2987 10.8855 9.82385 10.8855 9.53095 10.5926L6.99943 8.06107L4.46783 10.5927C4.17494 10.8856 3.70006 10.8856 3.40717 10.5927C3.11428 10.2998 3.11428 9.8249 3.40717 9.53201L5.93877 7.00041L3.40717 4.46881Z"
                      />
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">{placeholder}</span>
            )}
          </div>

          <div className="flex items-start pt-1.5">
            <svg
              className={cn(
                'h-5 w-5 shrink-0 text-gray-500 transition-transform dark:text-gray-400',
                isOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div
            className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-900"
            role="listbox"
            aria-multiselectable="true"
          >
            {searchable && (
              <div className="border-b border-gray-200 p-2 dark:border-gray-800">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="max-h-64 overflow-y-auto p-1" style={{ maxHeight }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                        option.disabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800',
                        isSelected && 'bg-brand-50 dark:bg-brand-500/10'
                      )}
                      onClick={() => !option.disabled && toggleOption(option.id)}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled}
                    >
                      <div
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded border',
                          isSelected
                            ? 'border-brand-500 bg-brand-500'
                            : 'border-gray-300 dark:border-gray-700'
                        )}
                      >
                        {isSelected && (
                          <svg
                            className="h-3 w-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{option.label}</span>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
